<template>
  <v-container class="page narrow" style="height: 100%">
    <Toolbar
      :loading="isLoading && legalEntities.length > 0"
      :title="$vuetify.breakpoint.xsOnly ? $t('message.payment_methods.title') : null"
    />
    <GenericError v-if="error" />
    <v-row
      v-else-if="(isLoading && legalEntities.length === 0) || deleteLoading"
      align="center"
      justify="center"
      class="fill-height"
    >
      <v-progress-circular indeterminate size="50" color="primary"></v-progress-circular>
    </v-row>
    <template v-else-if="legalEntities">
      <template v-for="legalEntity in legalEntities">
        <v-card v-if="legalEntity.stripeCustomer" :key="legalEntity['@id']" outlined class="mb-6">
          <v-card-title>
            <div>
              {{ legalEntity.name }}
            </div>
            <v-spacer />
            <v-btn
              v-if="legalEntity.stripePaymentMethods && legalEntity.stripePaymentMethods.length > 0"
              depressed
              color="primary"
              @click="addCard(legalEntity)"
            >
              <v-icon left>mdi-plus</v-icon> {{ $t('message.add_card') }}
            </v-btn>
          </v-card-title>
          <div v-if="legalEntity.stripePaymentMethods && legalEntity.stripePaymentMethods.length > 0">
            <v-list two-line class="pa-0">
              <template v-for="(paymentMethod, index) in legalEntity.stripePaymentMethods">
                <v-divider v-if="index > 0" :key="index" :inset="true"></v-divider>
                <v-list-item :key="paymentMethod.id">
                  <v-list-item-avatar>
                    <v-img
                      :src="
                        require('@/assets/img/payment_methods/' +
                          paymentMethod.card.brand.toLowerCase().replace(/ /g, '-') +
                          '.svg')
                      "
                      alt="Credit Card Icons"
                    />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ getCardLabel(paymentMethod.card) }}
                      <span v-if="paymentMethod.billing_details.name" class="grey--text text--darken-2"
                        >({{ paymentMethod.billing_details.name }})</span
                      >
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $t('message.expires_in') }}

                      {{ $moment.months(paymentMethod.card.exp_month - 1) }}
                      <template v-if="'es' === $i18n.locale">de</template>
                      {{ paymentMethod.card.exp_year }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn
                      icon
                      @click="paymentMethodToDelete = { legalEntity: legalEntity, paymentMethod: paymentMethod }"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-list>
          </div>
          <div v-else class="py-12 text-center">
            <div class="mb-3">
              <v-icon size="64" color="grey">mdi-credit-card-off-outline</v-icon>
            </div>
            <div class="headline grey--text mb-6">
              {{ $t('message.no_payment_methods_provided') }}
            </div>
            <div>
              <v-btn depressed color="primary" @click="addCard(legalEntity)"
                ><v-icon left>mdi-plus</v-icon> {{ $t('message.add_card') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </template>

      <v-dialog v-model="createDialog" max-width="400" persistent>
        <PaymentMethodCreate
          v-if="selectedLegalEntity"
          :legal-entity="selectedLegalEntity"
          @created="cardCreated"
          @close="createDialog = false"
        />
      </v-dialog>

      <v-dialog v-model="deleteDialog" persistent max-width="400">
        <v-card>
          <v-card-title class="headline">{{ $t('message.delete_payment_method_title') }}</v-card-title>
          <v-row v-if="!paymentMethodToDeleteAgreements" justify="center" class="py-12" no-gutters>
            <v-progress-circular color="primary" indeterminate></v-progress-circular>
          </v-row>
          <template v-else>
            <template v-if="false === canDelete(paymentMethodToDeleteAgreements)">
              <v-card-text>
                {{ $t('message.payment_method_has_active_agreement') }}
              </v-card-text>
              <PaymentMethodSelector
                v-if="paymentMethodToDelete"
                :legal-entity-id="paymentMethodToDelete.legalEntity['@id']"
                :exclude-payment-method="paymentMethodToDelete.paymentMethod.id"
                toolbar-margin="mx-4"
                @change="setNewPaymentMethod"
                @cardCreated="cardCreated"
              />
            </template>
            <template v-else>
              <v-card-text
                v-if="paymentMethodToDelete"
                v-html="
                  $t('message.delete_payment_method_paragraph', {
                    card: getCardLabel(paymentMethodToDelete.paymentMethod.card)
                  })
                "
              >
              </v-card-text>
            </template>
            <v-divider />
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="cancelDelete()">{{ $t('message.cancel') }}</v-btn>
              <v-btn :disabled="deleteDisabled" color="primary" text @click="deleteSelectedCard()">
                <template v-if="true === canDelete(paymentMethodToDeleteAgreements)">
                  {{ $t('message.delete') }}
                </template>
                <template v-else>
                  {{ $t('message.replace') }}
                </template>
              </v-btn>
            </v-card-actions>
          </template>
        </v-card>
      </v-dialog>
    </template>
  </v-container>
</template>

<script>
import GenericError from '@/views/error/GenericError'
import { mapActions, mapGetters } from 'vuex'
import Toolbar from '@/components/Toolbar'
import PaymentMethodCreate from '@/views/payment_method/Create'
import fetch from '@/utils/fetch'
import PaymentMethodSelector from '@/components/ActivationCreateDialog/PaymentMethodSelector'

export default {
  name: 'PaymentMethodList',
  components: {
    PaymentMethodSelector,
    PaymentMethodCreate,
    GenericError,
    Toolbar
  },
  data() {
    return {
      createDialog: false,
      deleteDialog: false,
      paymentMethodToDelete: null,
      deleteLoading: false,
      selectedLegalEntity: null,
      paymentMethodToDeleteAgreements: null,
      updatedAgreementsCount: 0,
      deleteDisabled: false
    }
  },

  computed: {
    ...mapGetters({
      legalEntities: 'legalentity/list/items',
      isLoading: 'legalentity/list/isLoading',
      error: 'legalentity/list/error'
    })
  },

  watch: {
    paymentMethodToDelete(newValue) {
      if (newValue === null) {
        this.deleteDialog = false
        this.paymentMethodToDeleteAgreements = null
      } else {
        this.deleteDialog = true
        fetch(
          '/connect_agreements?pagination=false&servicePlan.type=fixed_term&stripeSourceId=' + newValue.paymentMethod.id
        )
          .then(response => response.json())
          .then(data => {
            this.paymentMethodToDeleteAgreements = data['hydra:member']
            this.deleteDisabled = false === this.canDelete(this.paymentMethodToDeleteAgreements)
          })
          .catch(e => {
            // eslint-disable-next-line no-console
            console.log(e)
          })
      }
    }
  },

  mounted() {
    this.getLegalEntities()
  },

  methods: {
    ...mapActions({
      getPage: 'legalentity/list/default'
    }),

    addCard(legalEntity) {
      this.selectedLegalEntity = legalEntity
      this.createDialog = true
    },

    cardCreated() {
      this.createDialog = false
      this.getLegalEntities()
    },

    deleteSelectedCard() {
      if ('newPaymentMethod' in this.paymentMethodToDelete) {
        this.paymentMethodToDeleteAgreements.forEach(agreement => {
          this.updateAgreementCard(agreement, this.paymentMethodToDelete.newPaymentMethod)
        })
      } else {
        this.deleteCard()
      }
    },

    updateAgreementCard(agreement, newCard) {
      this.deleteLoading = true
      fetch(agreement['@id'], {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': 'application/ld+json' }),
        body: JSON.stringify({ stripeSourceId: newCard.id })
      })
        .then(response => response.json())
        .then(() => {
          this.deleteLoading = false
          this.updatedAgreementsCount += 1

          if (this.updatedAgreementsCount === this.paymentMethodToDeleteAgreements.length) {
            this.updatedAgreementsCount = 0
            this.deleteCard()
          }
        })
    },

    deleteCard() {
      this.deleteLoading = true
      fetch(this.paymentMethodToDelete.legalEntity['@id'] + '/delete_source', {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': 'application/ld+json' }),
        body: JSON.stringify({ sourceId: this.paymentMethodToDelete.paymentMethod.id })
      })
        .then(response => response.json())
        .then(() => {
          this.deleteLoading = false
          this.getLegalEntities()
          this.paymentMethodToDelete = null
          this.paymentMethodToDeleteAgreements = null
        })
        .catch(() => {
          this.deleteLoading = false
          this.paymentMethodToDelete = null
        })
    },

    cancelDelete() {
      this.paymentMethodToDelete = null
      this.deleteDialog = false
      this.paymentMethodToDeleteAgreements = null
    },

    getCardLabel(card) {
      return this.$t('message.payment_methods.brand.' + card.brand) + ' •••• ' + card.last4
    },

    canDelete(agreements) {
      return (
        agreements.filter(agreement => {
          return !agreement.stripeSourceDeletable
        }).length === 0
      )
    },

    setNewPaymentMethod(paymentMethod) {
      this.paymentMethodToDelete.newPaymentMethod = paymentMethod
      this.deleteDisabled = false
    },

    getLegalEntities() {
      this.getPage('/legal_entities?pagination=false&stripe=true')
    }
  }
}
</script>
